import Modal from '@/components/base/Modal';
import { formatMoney } from '@/lib/format';
import { differenceLabel, type CashSummary } from '@/lib/cashSession';

interface CloseSessionModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  processing: boolean;
  summary: CashSummary;
  cashCounted: number;
  currencySymbol: string;
  userName: string;
  periodLabel: string;
}

function Row({
  label,
  value,
  strong,
  accent,
}: {
  label: string;
  value: string;
  strong?: boolean;
  accent?: boolean;
}) {
  return (
    <div className="flex items-center justify-between gap-4 py-1.5">
      <span className="text-foreground-600">{label}</span>
      <span
        className={`${strong ? 'font-heading font-bold' : 'font-medium'} ${
          accent ? 'text-accent-700' : 'text-foreground-950'
        }`}
      >
        {value}
      </span>
    </div>
  );
}

export default function CloseSessionModal({
  open,
  onClose,
  onConfirm,
  processing,
  summary,
  cashCounted,
  currencySymbol,
  userName,
  periodLabel,
}: CloseSessionModalProps) {
  const difference = Math.round((cashCounted - summary.cashExpected) * 100) / 100;
  const meta = differenceLabel(difference);

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="¿Deseas cerrar el cuadre?"
      footer={
        <>
          <button
            onClick={onClose}
            disabled={processing}
            className="rounded-md border border-background-300 px-4 py-2.5 text-sm font-medium text-foreground-700 hover:bg-background-100 whitespace-nowrap transition disabled:opacity-60 cursor-pointer"
          >
            Cancelar
          </button>
          <button
            onClick={onConfirm}
            disabled={processing}
            className="inline-flex items-center justify-center gap-2 rounded-md bg-primary-500 hover:bg-primary-600 px-4 py-2.5 text-sm font-semibold text-background-50 whitespace-nowrap transition disabled:opacity-60 cursor-pointer"
          >
            {processing ? (
              <i className="ri-loader-4-line animate-spin" />
            ) : (
              <i className="ri-lock-line" />
            )}
            Confirmar cierre
          </button>
        </>
      }
    >
      <div className="divide-y divide-background-100">
        <Row label="Responsable" value={userName} />
        <Row label="Período" value={periodLabel} />
        <Row label="Ventas" value={String(summary.salesCount)} />
        <Row label="Total vendido" value={formatMoney(summary.total, currencySymbol)} />
        <Row label="Ganancia" value={formatMoney(summary.profit, currencySymbol)} strong accent />
        <Row label="Margen promedio" value={`${summary.margin.toFixed(1)}%`} />
        <Row label="Efectivo esperado" value={formatMoney(summary.cashExpected, currencySymbol)} />
        <Row label="Efectivo contado" value={formatMoney(cashCounted, currencySymbol)} />
        <Row
          label="Diferencia"
          value={formatMoney(difference, currencySymbol)}
          strong
          accent={meta.kind !== 'ok'}
        />
      </div>
      {meta.kind !== 'ok' && (
        <p className="mt-3 rounded-md bg-accent-50 border border-accent-200 px-3 py-2 text-sm font-medium text-accent-900">
          {meta.title}: {formatMoney(Math.abs(difference), currencySymbol)}
        </p>
      )}
      <p className="mt-3 text-xs text-foreground-500">
        Una vez cerrado, el cuadre no podrá modificarse sin autorización administrativa.
      </p>
    </Modal>
  );
}